import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  CompensationMetricsTimeseriesResponse,
  HttpResponse,
  StatewiseCompensationCount,
  StatewiseCompensationMetricsTableRow,
  StatewiseCompensationTimeseriesPoint,
} from "@/types";

const TWELVE_HOURS_IN_MS = 1000 * 60 * 60 * 12;

const QUERY_KEY = [
  "metrics/scraped-jobs/compensation-metrics-by-state/timeseries",
];

const queryFn = async () => {
  const path = "metrics/scraped-jobs/compensation-metrics-by-state/timeseries";
  const res = await guacHttpClient.get<
    CompensationMetricsTimeseriesResponse,
    HttpResponse<CompensationMetricsTimeseriesResponse>
  >(path);
  return res.data;
};

// Function overloads
export function useGetStatewiseCompensationMetricsTimeseries(
  state: string,
): UseQueryResult<StatewiseCompensationTimeseriesPoint[]>;
export function useGetStatewiseCompensationMetricsTimeseries(
  state: null,
): UseQueryResult<StatewiseCompensationMetricsTableRow[]>;
export function useGetStatewiseCompensationMetricsTimeseries(
  state: string | null,
): UseQueryResult<
  | StatewiseCompensationTimeseriesPoint[]
  | StatewiseCompensationMetricsTableRow[]
> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn,
    staleTime: TWELVE_HOURS_IN_MS,
    select: (raw: CompensationMetricsTimeseriesResponse) => {
      // Latest entry in the timeseries
      if (state === null) {
        const latest = raw[raw.length - 1];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!latest) return [];
        return Object.entries(
          latest.statesWithCounts as Record<
            string,
            StatewiseCompensationCount | undefined
          >,
        )
          .filter(
            (entry): entry is [string, StatewiseCompensationCount] =>
              entry[1] !== undefined,
          )
          .map(([s, counts]) => ({
            state: s,
            totalJobs: counts.totalCount,
            jobsWithCompensation: counts.compensationCount,
            compensationPercent: counts.compensationPercent,
          }));
      }

      // Timeseries
      return [...raw].map((entry) => {
        const counts = entry.statesWithCounts as Record<
          string,
          StatewiseCompensationCount | undefined
        >;
        const stateCounts = counts[state];
        const jobsWithComp = stateCounts?.compensationCount ?? 0;
        const total = stateCounts?.totalCount ?? 0;
        return {
          date: new Date(entry.date).toLocaleDateString("en-CA"),
          jobsWithComp,
          jobsWithoutComp: Math.max(0, total - jobsWithComp),
          compPercent: stateCounts?.compensationPercent ?? 0,
        };
      });
    },
  });
}
